import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    Input,
    OnInit,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IFormCheckbox } from '../../_shared/models/form/form-checkbox.model';
import { ButtonComponent } from '../../button/button.component';
import { Modal2Component } from '../../modal/modal2/modal2.component';
import { TextHtmlComponent } from '../../text-html/text-html.component';
import { FormElementBase } from '../_directives/form-element-base.directive';
import { CONSENT_MODAL_LINK_SELECTOR } from './consent-modal-link.constants';
import { getConsentVariantByIdentifier } from './consent-variants/consent-variant-registry';

export interface IConsentDialogContent {
    bodyHtmlKey: string;
    buttonTextKey: string;
}

@Component({
    selector: 'sirius-form-consent-checkbox',
    templateUrl: './form-consent-checkbox.component.html',
    styleUrls: ['./form-consent-checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        ButtonComponent,
        Modal2Component,
        TextHtmlComponent,
        TranslateModule,
    ]
})
export class FormConsentCheckboxComponent extends FormElementBase implements OnInit {
    config?: IFormCheckbox;

    @Input() labelHtmlKey = '';
    @Input() labelHtmlTranslateParams: Record<string, string> = {};
    @Input() errorText = 'Please accept this consent to continue.';
    @Input() dialogContent?: IConsentDialogContent;
    readonly dialogOpen = signal(false);
    private _cdr = inject(ChangeDetectorRef);
    private _destroyRef = inject(DestroyRef);

    get errorId(): string {
        return `${this.id}-error`;
    }

    get dialogBodyId(): string {
        return `${this.id}-dialog-body`;
    }

    get showError(): boolean {
        const control = this.formControl;
        if (!control) {
            return false;
        }
        return control.invalid && (control.dirty || control.touched);
    }

    get isChecked(): boolean {
        const control = this.formControl;
        return `${control?.value ?? ''}` === this._checkedValue;
    }

    get isDisabled(): boolean {
        const control = this.formControl;
        return control?.disabled ?? false;
    }

    private _checkedValue = 'on';

    onChange(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.updateControlValue(checked);
    }

    ngOnInit(): void {
        if (this.config) {
            this.name = this.config.name;
            this.label = this.config.label;
            this.formFieldDependencies = this.config.dependentFormFieldComponents;
            this.validation = this.config.validation;
            this.form = this.config.form;
            this.identifier = this.config.identifier ?? '';
            this._checkedValue = this.config.value || 'on';

            const variant = getConsentVariantByIdentifier(this.identifier);
            if (variant) {
                const presentation = variant.createPresentation();
                this.labelHtmlKey = presentation.labelHtmlKey;
                this.labelHtmlTranslateParams = presentation.labelHtmlTranslateParams;
                this.dialogContent = presentation.dialogContent;
            } else {
                this.labelHtmlKey = this.label;
            }

            // Only seed the control on the very first ngOnInit. If this
            // component is re-mounted (e.g. an Angular control-flow branch
            // remounts the surrounding form), the control already holds the
            // user's previous selection — clobbering it here would silently
            // un-check the consent and surface as a "consent not clicked"
            // submit failure even though the user did click it.
            const control = this.formControl;
            if (control && !this.config.checked && !control.dirty && !control.touched) {
                control.setValue('');
            }
        }

        this.formControl?.statusChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._cdr.markForCheck());
    }

    onLabelContentClick(event: MouseEvent): void {
        if (!this.dialogContent) {
            return;
        }

        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const anchor = target.closest(CONSENT_MODAL_LINK_SELECTOR);
        if (!anchor) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.dialogOpen.set(true);
    }

    onDialogConfirm(): void {
        this.updateControlValue(true);
        this.dialogOpen.set(false);
    }

    onBlur(): void {
        this.formControl?.markAsTouched();
    }

    get isRequired(): boolean {
        return this.validation.includes('required') || this.validation.includes('requiredTrue');
    }

    private updateControlValue(checked: boolean): void {
        const control = this.formControl;
        if (!control) {
            return;
        }

        control.setValue(checked ? this._checkedValue : '');
        control.markAsTouched();
        control.markAsDirty();
    }

    private get formControl(): FormControl | null {
        if (!this.name) {
            return null;
        }

        return this.form.get(this.name) as FormControl | null;
    }
}
